// src/screens/AdminValidacionProveedoresScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline,
    IoDocumentTextOutline,
    IoLogoFacebook,
    IoLogoInstagram,
    IoLogoTwitter,
    IoLogoTiktok,
    IoGlobeOutline,
    IoHomeOutline,
    IoWarningOutline,
    IoShieldCheckmarkOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './AdminValidacionProveedoresScreen.css';

function AdminValidacionProveedoresScreen() {
    const navigate = useNavigate();
    const { userData } = useAuth(); // Necesitamos el condominioId del admin
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (userData?.condominioId) {
            fetchSolicitudes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const fetchSolicitudes = async () => {
        setLoading(true);
        try {
            // 1. Buscar proveedores con estatus 'en_revision' Y del mismo condominio
            const q = query(
                collection(db, "market_proveedores"),
                where("estatus", "==", "en_revision"),
                where("condominioId", "==", userData.condominioId)
            );
            const querySnapshot = await getDocs(q);

            const lista = await Promise.all(querySnapshot.docs.map(async (providerDoc) => {
                const providerData = providerDoc.data();

                // 2. Obtener datos extra del usuario (Unidad, Estatus Financiero)
                let usuarioInfo = { unidad: 'N/A', estadoCuenta: 'Desconocido' };

                // Usamos usuarioId del documento, o el ID del documento mismo (que debería ser el UID)
                const targetUserId = providerData.usuarioId || providerDoc.id;

                if (targetUserId) {
                    try {
                        const userDocRef = doc(db, "usuarios", targetUserId);
                        const userSnap = await getDoc(userDocRef);
                        if (userSnap.exists()) {
                            const uData = userSnap.data();
                            usuarioInfo = {
                                unidad: uData.numeroCasa || 'Sin Asignar',
                                estadoCuenta: uData.isMoroso ? 'Moroso' : 'Al Corriente'
                            };
                        } else {
                            console.warn(`Usuario no encontrado para ID: ${targetUserId}`);
                        }
                    } catch (e) {
                        console.error("Error fetching user details", e);
                    }
                }

                return {
                    id: providerDoc.id,
                    ...providerData,
                    ...usuarioInfo
                };
            }));

            setSolicitudes(lista);
        } catch (err) {
            console.error("Error al cargar solicitudes:", err);
            setError("Error al cargar las solicitudes pendientes.");
        } finally {
            setLoading(false);
        }
    };

    const handleAprobar = async (proveedorId, nombre) => {
        if (!window.confirm(`¿Estás seguro de APROBAR la solicitud de "${nombre}"?\n\nPasará a revisión del Superuser.`)) return;

        try {
            const docRef = doc(db, "market_proveedores", proveedorId);
            await updateDoc(docRef, {
                estatus: "aprobado_admin"
            });
            alert(`Solicitud de "${nombre}" aprobada. Ahora está pendiente de Superuser.`);
            fetchSolicitudes(); // Recargar lista
        } catch (err) {
            console.error("Error al aprobar:", err);
            alert("Error al actualizar el estatus.");
        }
    };

    const handleRechazar = async (proveedorId, nombre) => {
        const motivo = window.prompt(`Ingresa el motivo del rechazo para "${nombre}" (Obligatorio):`);

        if (motivo === null) return; // Cancelado
        if (!motivo.trim()) {
            alert("El motivo de rechazo es obligatorio.");
            return;
        }

        try {
            const docRef = doc(db, "market_proveedores", proveedorId);
            await updateDoc(docRef, {
                estatus: "rechazado",
                motivoRechazo: motivo
            });
            alert(`Solicitud de "${nombre}" rechazada.`);
            fetchSolicitudes();
        } catch (err) {
            console.error("Error al rechazar:", err);
            alert("Error al actualizar el estatus.");
        }
    };

    const renderSocialLink = (url, icon) => {
        if (!url) return null;
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0d9488', fontSize: '1.2em' }}>
                {icon}
            </a>
        );
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/admin/validacion-proveedores" />

            <main className="ks-main">
                <KondoBrandHeader title="Administración" showBackButton={false} />

                <div className="validacion-container">
                    {/* --- Premium Banner --- */}
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Validación de Proveedores</h1>
                                <p>Revisa las solicitudes de residentes para unirse al Marketplace.</p>
                            </div>

                            <IoShieldCheckmarkOutline className="muro-hero-icon" />
                        </div>
                    </div>

            {loading ? (
                <div className="empty-state"><p>Cargando solicitudes...</p></div>
            ) : error ? (
                <p className="empty-state" style={{ color: '#dc2626' }}>{error}</p>
            ) : solicitudes.length === 0 ? (
                <div className="empty-state">
                    <IoCheckmarkCircleOutline size={50} style={{ opacity: 0.3 }} />
                    <p>No hay solicitudes pendientes en este condominio.</p>
                </div>
            ) : (
                /* --- GRID LAYOUT --- */
                <div className="requests-grid">
                    {solicitudes.map(solicitud => (
                        <div key={solicitud.id} className="request-card">

                            <div className="request-header">
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#111827' }}>{solicitud.nombrePublico}</h3>
                                    <span style={{ fontSize: '0.9em', color: '#6b7280' }}>Categoría: {solicitud.categoria}</span>
                                </div>
                                <span className="request-category">{solicitud.categoria}</span>
                            </div>

                            <div className="request-body">

                                <div className="info-section">
                                    <div className="info-title">Descripción</div>
                                    <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: '1.5', margin: 0 }}>
                                        {solicitud.descripcion || 'Sin descripción'}
                                    </p>
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                        {renderSocialLink(solicitud.sitioWeb, <IoGlobeOutline />)}
                                        {renderSocialLink(solicitud.redesSociales?.facebook, <IoLogoFacebook />)}
                                        {renderSocialLink(solicitud.redesSociales?.instagram, <IoLogoInstagram />)}
                                        {renderSocialLink(solicitud.redesSociales?.twitter, <IoLogoTwitter />)}
                                        {renderSocialLink(solicitud.redesSociales?.tiktok, <IoLogoTiktok />)}
                                    </div>
                                </div>

                                <div className="info-section">
                                    <div className="info-title"><IoHomeOutline /> Datos del Residente</div>
                                    <div className="resident-info-box">
                                        <div style={{ fontWeight: 600 }}>{solicitud.nombreContacto}</div>
                                        <div style={{ fontSize: '0.9em', color: '#6b7280' }}>Unidad: {solicitud.unidad}</div>

                                        <div className="status-row">
                                            Estatus:
                                            {solicitud.estadoCuenta === 'Moroso' ? (
                                                <span className="status-moroso"><IoWarningOutline /> Moroso</span>
                                            ) : (
                                                <span className="status-aldia"><IoCheckmarkCircleOutline /> Al Corriente</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="info-section">
                                    <div className="info-title"><IoDocumentTextOutline /> Documentación</div>
                                    <div className="documents-list">
                                        {solicitud.documentos?.constanciaFiscalUrl ? (
                                            <a href={solicitud.documentos.constanciaFiscalUrl} target="_blank" rel="noopener noreferrer" className="doc-link">
                                                <IoDocumentTextOutline /> Constancia Fiscal
                                            </a>
                                        ) : <span style={{ fontSize: '0.85em', color: '#9ca3af' }}>Sin Constancia</span>}

                                        {solicitud.documentos?.identificacionOficialUrl ? (
                                            <a href={solicitud.documentos.identificacionOficialUrl} target="_blank" rel="noopener noreferrer" className="doc-link">
                                                <IoDocumentTextOutline /> Identificación (INE)
                                            </a>
                                        ) : <span style={{ fontSize: '0.85em', color: '#9ca3af' }}>Sin ID</span>}
                                    </div>
                                </div>

                            </div>

                            <div className="request-footer">
                                <button
                                    onClick={() => handleRechazar(solicitud.id, solicitud.nombrePublico)}
                                    className="action-btn btn-reject"
                                >
                                    <IoCloseCircleOutline size={20} /> Rechazar
                                </button>
                                <button
                                    onClick={() => handleAprobar(solicitud.id, solicitud.nombrePublico)}
                                    className="action-btn btn-approve"
                                >
                                    <IoCheckmarkCircleOutline size={20} /> Aprobar
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
                </div>
            </main>

            <KondoBottomNav activeRoute="/admin/validacion-proveedores" />
        </div>
    );
}

export default AdminValidacionProveedoresScreen;
